import React, { useEffect, useState, useMemo, useRef } from 'react';
import RightDrawer from '../RightDrawer';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import {
    Room,
    RoomComplementaryMapping,
    RoomFacilityMapping,
    RMCRoomCharge,
    RoomCharge,
} from '../../models/room.model';
import { RoomTypeCategory } from '../../models/roomTypeCategory.model';
import { RoomType } from '../../models/roomType.model';
import { Facility } from '../../models/facility.model';
import { roomService } from '../../service/room.service';
import { roomTypeService } from '../../service/roomType.service';
import { categoryService } from '../../service/category.service';
import { facilityService } from '../../service/facilites.service';
import { complementaryService } from '../../service/complementary.service';
import { ListItem } from '../../models/ListItem';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { upload } from '@testing-library/user-event/dist/upload';
import RoomGallery from './RoomGallery';
// uuid import removed (not needed)

interface RoomDetailsProps {
    show: boolean;
    onClose: () => void;
    propertyId: number;
    roomId?: number;
    rmcClientMappingId: number;
    propertyName: string;
    onRoomSaved?: () => void;
    asDrawer?: boolean; // render in right side panel instead of modal
    prefetchedRoom?: any; // optional room object from list containing roomImages
    readOnlyMode?: boolean; // whether the form should be in read-only mode
}

const RoomDetails: React.FC<RoomDetailsProps> = ({
    show,
    onClose,
    propertyId,
    roomId,
    rmcClientMappingId,
    propertyName,
    onRoomSaved,
    asDrawer = false,
    prefetchedRoom,
    readOnlyMode = false
}) => {
    const [room, setRoom] = useState<Partial<Room>>({});
    const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
    const [categories, setCategories] = useState<RoomTypeCategory[]>([]);
    const [facilitiesList, setFacilitiesList] = useState<Facility[]>([]);
    const [complementariesList, setComplementariesList] = useState<ListItem[]>([]);
    const [roomCharge, setRoomCharge] = useState<RoomCharge | null>(null);
    const [rmcRoomCharge, setRmcRoomCharge] = useState<RMCRoomCharge | null>(null);
    // Image management state
    const [existingImages, setExistingImages] = useState<any[]>([]); // from API (roomImages or imageDtos)
    const [newUploads, setNewUploads] = useState<File[]>([]);
    const [mainImageId, setMainImageId] = useState<number | null>(null);
    const [newMainUploadIndex, setNewMainUploadIndex] = useState<number | null>(null);
    const [deleteImageIds, setDeleteImageIds] = useState<number[]>([]);
    // Derived gallery images (exactly 5 with placeholders) used for unified slider display
    const PLACEHOLDER_IMAGE = useMemo(() => {
        // Neutral SVG placeholder (gray background, camera icon-ish)
        const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400' viewBox='0 0 600 400'>
    <rect width='600' height='400' fill='#f2f2f2'/>
    <g fill='none' stroke='#cccccc' stroke-width='14' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M180 140h60l24-32h72l24 32h60c22 0 40 18 40 40v120c0 22-18 40-40 40H180c-22 0-40-18-40-40V180c0-22 18-40 40-40z' />
        <circle cx='300' cy='240' r='60' />
        <circle cx='410' cy='180' r='16' />
    </g>
</svg>`;
        return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
    }, []);
    const [galleryImages, setGalleryImages] = useState<any[]>([]);
    const userRole = useSelector((state: any) => state.auth.userRole) ?? '';
    const isReadOnly = userRole === 'RMCAdmin' || userRole === 'RMCUser';

    // Keep previous show state to detect transitions (open -> closed)
    const prevShowRef = useRef<boolean>(show);

    // When the drawer is used (asDrawer=true) and it transitions from open -> closed,
    // reset the form state so the next open shows a fresh form (for both add and edit flows).
    useEffect(() => {
        if (asDrawer && prevShowRef.current && !show) {
            // Reset all form-related state
            setRoom({});
            setRoomTypes([]);
            setCategories([]);
            setFacilitiesList([]);
            setComplementariesList([]);
            setRoomCharge(null);
            setRmcRoomCharge(null);
            setExistingImages([]);
            setNewUploads([]);
            setMainImageId(null);
            setNewMainUploadIndex(null);
            setDeleteImageIds([]);
            setGalleryImages([]);
        }
        prevShowRef.current = show;
    }, [show, asDrawer]);



    const extractImages = (source: any): any[] => {
        const candidate =
            (source?.imageDtos && (source.imageDtos.$values || source.imageDtos)) ||
            (source?.roomImages && (source.roomImages.$values || source.roomImages)) ||
            (source?.ImageDtos && (source.ImageDtos.$values || source.ImageDtos)) ||
            (source?.RoomImages && (source.RoomImages.$values || source.RoomImages)) ||
            [];
        const arr = Array.isArray(candidate) ? candidate : [];
        return arr.map((i: any) => ({
            id: i.id,
            imageUrl: i.imageUrl || i.ImageUrl || '',
            fileName: i.fileName || i.FileName || '',
            isMainImage: i.isMainImage || i.IsMainImage || false
        }));
    };

    // Seed with prefetched images immediately for faster UI feedback
    useEffect(() => {
        if (prefetchedRoom && existingImages.length === 0) {
            const preImgs = extractImages(prefetchedRoom);
            if (preImgs.length) {
                setExistingImages(preImgs);
                const main = preImgs.find(i => i.isMainImage);
                setMainImageId(main?.id || null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefetchedRoom]);

    const fetchRoomDetails = async () => {
        if (!roomId) return;

        try {
            const res = await roomService.getRoomByIdWithMapping(roomId, rmcClientMappingId) as any;

            if (res) {
                const normalizedRoom: Partial<Room> = {
                    ...res,
                    roomCharges: res.roomCharges?.$values ?? [],
                    rmcRoomCharges: res.rmcRoomCharges?.$values ?? [],
                    roomFacilityMappings: res.roomFacilityMappings?.$values ?? [],
                    roomComplementaryMappings: res.roomComplementaryMappings?.$values ?? [],
                    rmcRoomComplementaryMappings: res.rmcRoomComplementaryMappings?.$values ?? [],
                    rmcRoomFacilityMappings: res.rmcRoomFacilityMappings?.$values ?? [],
                };
                console.log('Room:', res);
                // console.log('roomCharges:', res.roomCharges);
                // console.log('rmcRoomCharges:', normalizedRoom.rmcRoomCharges);
                // console.log('facilityMappings:', normalizedRoom.roomFacilityMappings);
                // console.log('complementaryMappings:', normalizedRoom.roomComplementaryMappings);
                console.log('rmcComplementaryMappings:', normalizedRoom.rmcRoomComplementaryMappings);
                console.log('rmcFacilityMappings:', normalizedRoom.rmcRoomFacilityMappings);

                setRoom(res);
                setRoomCharge(res.roomCharges?.[0] ?? null);
                setRmcRoomCharge(res.rmcRoomCharges?.[0] ?? null);
                let imgs = extractImages(res);
                if (imgs.length === 0 && prefetchedRoom) {
                    const fallback = extractImages(prefetchedRoom);
                    if (fallback.length) {
                        console.debug('[roomAddEdit] Using prefetched roomImages fallback');
                        imgs = fallback;
                    }
                }
                if (imgs.length === 0) {
                    console.debug('[roomAddEdit] No room images found (after fallback). Keys:', Object.keys(res || {}));
                }
                if (imgs.length) {
                    setExistingImages(imgs);
                    const main = imgs.find(i => i.isMainImage);
                    setMainImageId(main?.id || null);
                }
            }
        } catch (err) {
            console.error('Error:', err);
            setRoom({});
            setRoomCharge(null);
            setRmcRoomCharge(null);
            setExistingImages([]);
        }
    };




    useEffect(() => {
        const loadData = async () => {
            await Promise.all([
                roomTypeService.getRoomTypesByProperty(propertyId).then(setRoomTypes).catch(() => setRoomTypes([])),
                facilityService.getAll().then(setFacilitiesList).catch(() => setFacilitiesList([])),
                complementaryService.getAll().then(setComplementariesList).catch(() => setComplementariesList([])),
            ]);

            await fetchRoomDetails(); // only after lists are ready
        };

        loadData();
    }, [roomId, propertyId]);
    const handleChange = (field: keyof Room, value: any) => {
        setRoom((prev) => ({ ...prev, [field]: value }));
    };
    useEffect(() => {
        const fetchCategories = async () => {
            if (!room.roomTypeId) {
                setCategories([]);
                return;
            }

            try {
                const result = await categoryService.getByRoomTypeId(room.roomTypeId);
                setCategories(result ?? []);
            } catch (err) {
                console.error("Error fetching categories:", err);
                setCategories([]);
            }
        };

        fetchCategories();
    }, [room.roomTypeId]); // 👈 ONLY runs when roomTypeId changes

    const handleSave = async () => {
        let updatedRmcRoomCharge = rmcRoomCharge;

        // Check if rmcRoomCharge has values and rmcClientMappingId is 0, then set it from props
        if (
            updatedRmcRoomCharge &&
            (updatedRmcRoomCharge.amount || updatedRmcRoomCharge.taxPercentage) &&
            (updatedRmcRoomCharge.proposalPropertyMappingId === 0 || updatedRmcRoomCharge.proposalPropertyMappingId === undefined)
        ) {
            updatedRmcRoomCharge = {
                ...updatedRmcRoomCharge,
                proposalPropertyMappingId: rmcClientMappingId
            };
        }
        const uploadedFiles = room.uploadedFiles
        const updatedRoom: Partial<Room> = {
            ...room,
            propertyId,
            roomCharges: roomCharge ? [roomCharge] : [],
            rmcRoomCharges: updatedRmcRoomCharge ? [updatedRmcRoomCharge] : [],
            uploadedFiles: newUploads,
        };

        // Attach image management metadata (custom fields consumed by updateRoom FormData builder)
        (updatedRoom as any).mainImageId = mainImageId || undefined;
        (updatedRoom as any).deleteImageIds = deleteImageIds.length ? deleteImageIds : undefined;
        (updatedRoom as any).newMainUploadIndex = newMainUploadIndex !== null ? newMainUploadIndex : undefined;

        if (roomId) {
            await roomService.updateRoom(roomId, updatedRoom as any);
            Swal.fire({
                toast: true,
                position: 'bottom',
                icon: 'success',
                title: 'Room updated successfully.',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: { popup: 'swal2-toast-bottom' }
            });
        } else {
            const createPayload: any = { ...updatedRoom };
            // For create we piggyback on createRoom (which expects uploadedFiles -> ImageDtos)
            await roomService.createRoom(createPayload as Room);
            Swal.fire({
                toast: true,
                position: 'bottom',
                icon: 'success',
                title: 'Room created successfully.',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: { popup: 'swal2-toast-bottom' }
            });
        }

        setTimeout(() => {
            onRoomSaved?.();
            onClose();
        }, 500); // Give time for the toast to show before closing
    };

    const mapSelectedFacilities = (): { label: string; value: number }[] => {
        if (!Array.isArray(room.roomFacilityMappings)) return [];

        return room.roomFacilityMappings
            .map((mapping: any) => {
                const facility = facilitiesList.find(f => f.id === mapping.facilityId);
                return facility ? { label: facility.name, value: facility.id } : null;
            })
            .filter((v): v is { label: string; value: number } => v !== null);
    };

    const mapSelectedComplementaries = (): { label: string; value: number }[] => {
        if (!Array.isArray(room.roomComplementaryMappings)) return [];

        return room.roomComplementaryMappings
            .map((mapping: any) => {
                const comp = complementariesList.find(c => c.id === mapping.complementaryId);
                return comp ? { label: comp.name, value: comp.id } : null;
            })
            .filter((v): v is { label: string; value: number } => v !== null);
    };
    const mapSelectedRMCFacilities = (): { label: string; value: number }[] => {
        if (!Array.isArray(room.rmcRoomFacilityMappings)) return [];

        return room.rmcRoomFacilityMappings
            .map((mapping: any) => {
                const facility = facilitiesList.find(f => f.id === mapping.facilityId);
                return facility ? { label: facility.name, value: facility.id } : null;
            })
            .filter((v): v is { label: string; value: number } => v !== null);
    };
    console.log('room.rmcRoomFacilityMappings:', mapSelectedRMCFacilities());
    const mapSelectedRMCComplementaries = (): { label: string; value: number }[] => {
        if (!Array.isArray(room.rmcRoomComplementaryMappings)) return [];

        return room.rmcRoomComplementaryMappings
            .map((mapping: any) => {
                const comp = complementariesList.find(c => c.id === mapping.complementaryId);
                return comp ? { label: comp.name, value: comp.id } : null;
            })
            .filter((v): v is { label: string; value: number } => v !== null);
    };
    console.log('room.rmcRoomComplementaryMappings:', mapSelectedRMCComplementaries());

    const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        if (!files.length) return;
        setNewUploads(prev => [...prev, ...files]);
        // Auto-select first as main if none chosen
        if (mainImageId == null && newMainUploadIndex == null && existingImages.every(i => !i.isMainImage)) {
            setNewMainUploadIndex(0);
        }
    };

    const toggleMainExisting = (imgId: number) => {
        setMainImageId(imgId);
        setNewMainUploadIndex(null);
    };

    const toggleMainNew = (index: number) => {
        setNewMainUploadIndex(index);
        setMainImageId(null);
    };

    const markDeleteExisting = (imgId: number) => {
        setDeleteImageIds(prev => prev.includes(imgId) ? prev.filter(id => id !== imgId) : [...prev, imgId]);
        if (mainImageId === imgId) setMainImageId(null);
    };

    const removeNewUpload = (index: number) => {
        setNewUploads(prev => prev.filter((_, i) => i !== index));
        if (newMainUploadIndex === index) setNewMainUploadIndex(null);
    };

    // Build galleryImages whenever existing/new uploads change
    useEffect(() => {
        type GalleryItem = { imageUrl: string; id?: number; isMainImage?: boolean; temp?: boolean; uploadIndex?: number; placeholder?: boolean };
        const existing: GalleryItem[] = existingImages
            .filter(i => !deleteImageIds.includes(i.id))
            .map(i => ({ imageUrl: i.imageUrl, id: i.id, isMainImage: i.isMainImage }));
        const uploads: GalleryItem[] = newUploads.map((f, idx) => ({ imageUrl: URL.createObjectURL(f), temp: true, uploadIndex: idx, isMainImage: newMainUploadIndex === idx }));
        const combined: GalleryItem[] = [...existing, ...uploads];
        if (combined.length === 0) {
            setGalleryImages(Array.from({ length: 5 }, () => ({ imageUrl: PLACEHOLDER_IMAGE, placeholder: true })));
            return;
        }
        while (combined.length < 5) combined.push({ imageUrl: PLACEHOLDER_IMAGE, placeholder: true });
        setGalleryImages(combined.slice(0, 5));
    }, [existingImages, newUploads, deleteImageIds, newMainUploadIndex, PLACEHOLDER_IMAGE]);

    const formContent = (
        <Form>
            {/* Gallery (preview slider) suppressed inside drawer to save vertical space */}
            {!asDrawer && (
                <div className="mb-4">
                    <h6 className="fw-semibold d-flex align-items-center justify-content-between">Room Images
                        <small className="text-muted ms-2">(Up to 5 shown)</small>
                    </h6>
                    <RoomGallery images={galleryImages} />
                </div>
            )}
            <Row className="mb-3">
                <Col>
                    <Form.Label>Upload / Manage Images</Form.Label>
                    <Form.Control 
                        type="file" 
                        multiple 
                        accept="image/*" 
                        onChange={handleFilesSelected}
                        disabled={readOnlyMode}
                    />
                    <small className="text-muted">Choose images (first auto main if none selected). Click Main/Delete below.</small>
                </Col>
            </Row>
            <Row className="g-2 mb-3">
                {existingImages.filter(i => !deleteImageIds.includes(i.id)).map(img => (
                    <Col xs={6} md={4} key={img.id} className="position-relative">
                        <div className={`border rounded p-1 h-100`}>
                            <img src={img.imageUrl || PLACEHOLDER_IMAGE} alt={img.fileName} style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: 4 }} />
                            {!readOnlyMode && (
                                <div className="d-flex justify-content-between mt-1">
                                    <Button size="sm" variant={mainImageId === img.id ? 'success' : 'outline-secondary'} onClick={() => toggleMainExisting(img.id)}>Main</Button>
                                    <Button size="sm" variant={'outline-danger'} onClick={() => markDeleteExisting(img.id)}>
                                        Delete
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Col>
                ))}
                {!readOnlyMode && newUploads.map((file, idx) => (
                    <Col xs={6} md={4} key={`new-${idx}`} className="position-relative">
                        <div className="border rounded p-1 h-100">
                            <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: 4 }} />
                            <div className="d-flex justify-content-between mt-1">
                                <Button size="sm" variant={newMainUploadIndex === idx ? 'success' : 'outline-secondary'} onClick={() => toggleMainNew(idx)}>Main</Button>
                                <Button size="sm" variant="outline-danger" onClick={() => removeNewUpload(idx)}>Remove</Button>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
            <Row className="mb-3">
                <Col>
                    <Form.Label>Room Type:</Form.Label>
                    <Form.Select
                        value={room.roomTypeId || ''}
                        onChange={(e) => handleChange('roomTypeId', +e.target.value)}
                        disabled={readOnlyMode}
                    >
                        <option value="">Select</option>
                        {roomTypes.map(rt => (
                            <option key={rt.id} value={rt.id}>{rt.name}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col>
                    <Form.Label>Room Category:</Form.Label>
                    <Form.Select
                        value={room.categoryId || ''}
                        onChange={(e) => {
                            const selectedCategoryId = +e.target.value;
                            const selectedCategory = categories.find(c => c.id === selectedCategoryId);
                            handleChange('categoryId', selectedCategoryId);
                            handleChange('categoryDescription', selectedCategory?.description || '');
                            handleChange('name', selectedCategory?.name);
                        }}
                        disabled={readOnlyMode}
                    >
                        <option value="">Select</option>
                        {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Description:</Form.Label>
                <Form.Control
                    as="textarea"
                    value={room.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    readOnly={readOnlyMode}
                />
            </Form.Group>

            {/* <Form.Group className="mb-3">
                        <Form.Label>Category Description:</Form.Label>
                        <Form.Control
                            as="textarea"
                            value={room.categoryDescription || ''}
                            onChange={(e) => handleChange('categoryDescription', e.target.value)}
                            readOnly={readOnlyMode}
                        />
                    </Form.Group> */}

            <Row className="mb-3">
                <Col>
                    <Form.Label>Area (sqft):</Form.Label>
                    <Form.Control
                        type="number"
                        value={room.area || ''}
                        onChange={(e) => handleChange('area', +e.target.value)}
                        readOnly={readOnlyMode}
                    />
                </Col>
                <Col>
                    <Form.Label>Number of Bedrooms:</Form.Label>
                    <Form.Control
                        type="number"
                        value={room.numberOfBedrooms || ''}
                        onChange={(e) => handleChange('numberOfBedrooms', +e.target.value)}
                        readOnly={readOnlyMode}
                    />
                </Col>
            </Row>

            <Row className="mb-3">
                <Col>
                    <Form.Label>Number of Bathrooms:</Form.Label>
                    <Form.Control
                        type="number"
                        value={room.numberOfBathrooms || ''}
                        onChange={(e) => handleChange('numberOfBathrooms', +e.target.value)}
                        readOnly={readOnlyMode}
                    />
                </Col>
                <Col>
                    <Form.Label>Room Inventory:</Form.Label>
                    <Form.Control
                        type="number"
                        value={room.totalInventory || ''}
                        onChange={(e) => handleChange('totalInventory', +e.target.value)}
                        readOnly={readOnlyMode}
                    />
                </Col>
            </Row>
            {userRole === 'Administrator' && (
                <Row className="mb-3">
                    {/* <Col>
                            <Form.Label>Room Facilities:</Form.Label>
                            <Select
                                isMulti
                                value={mapSelectedFacilities()}
                                options={facilitiesList.map(f => ({ label: f.name, value: f.id }))}
                                onChange={(selected) =>
                                    setRoom(prev => ({
                                        ...prev,
                                        roomFacilityMappings: selected.map((s: any) => ({
                                            id: 0,
                                            roomId: prev.id || 0,
                                            facilityId: s.value
                                        }))
                                    }))
                                }
                                isDisabled={readOnlyMode}
                            />

                        </Col> */}
                    <Col>
                        <Form.Label>Room Complementaries:</Form.Label>
                        <Select
                            isMulti
                            value={mapSelectedComplementaries()}
                            options={complementariesList.map(c => ({ label: c.name, value: Number(c.id) }))}
                            onChange={(selected) =>
                                setRoom(prev => ({
                                    ...prev,
                                    roomComplementaryMappings: selected.map(s => ({
                                        id: 0,
                                        roomId: prev.id || 0,
                                        complementaryId: s.value
                                    }))
                                }))
                            }
                            isDisabled={readOnlyMode}
                        />
                    </Col>
                </Row>
            )}
            {/* Charges fields only for Administrator */}
            {userRole === 'Administrator' && (
                <Row className="mb-3">
                    <Col>
                        <Form.Label>Charges:</Form.Label>
                        <Form.Control
                            type="number"
                            value={roomCharge?.amount || ''}
                            onChange={(e) =>
                                setRoomCharge(prev => ({
                                    ...prev,
                                    roomId: room.id || 0,
                                    amount: +e.target.value,
                                    taxPercentage: prev?.taxPercentage ?? 0,
                                } as RoomCharge))
                            }
                            readOnly={readOnlyMode}
                        />
                    </Col>
                    <Col>
                        <Form.Label>Tax Percentage:</Form.Label>
                        <Form.Control
                            type="number"
                            value={roomCharge?.taxPercentage ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setRoomCharge(prev => ({
                                    ...prev,
                                    roomId: room.id || 0,
                                    taxPercentage: +e.target.value
                                } as RoomCharge))
                            }
                            readOnly={readOnlyMode}
                        />
                    </Col>
                </Row>
            )}
            {/* Commented out RMC fields as requested
            <Row className="mb-3">
                <Col>
                    <Form.Label>RMC Charges:</Form.Label>
                    <Form.Control
                        type="number"
                        value={rmcRoomCharge?.amount || ''}
                        onChange={(e) =>
                            setRmcRoomCharge(prev => ({
                                ...prev,
                                roomId: room.id || 0,
                                proposalPropertyMappingId: prev?.proposalPropertyMappingId || 0,
                                amount: +e.target.value
                            }))
                        }
                        readOnly={true}
                    />
                </Col>
                <Col>
                    <Form.Label>RMC Tax Percentage:</Form.Label>
                    <Form.Control
                        type="number"
                        value={rmcRoomCharge?.taxPercentage || ''}
                        onChange={(e) =>
                            setRmcRoomCharge(prev => ({
                                ...prev,
                                roomId: room.id || 0,
                                proposalPropertyMappingId: prev?.proposalPropertyMappingId || 0,
                                taxPercentage: +e.target.value,
                                amount: prev?.amount ?? 0,
                            }))
                        }
                        readOnly={true}
                    />
                </Col>
            </Row>
            */}
            {/* Commented out RMC Room Complementaries
            <Row className="mb-3">
                <Col>
                    <Form.Label>RMC Room Complementaries:</Form.Label>
                    <Select
                        isMulti
                        value={mapSelectedRMCComplementaries()}
                        options={complementariesList.map(c => ({ label: c.name, value: Number(c.id) }))}
                        onChange={(selected) =>
                            setRoom(prev => ({
                                ...prev,
                                rmcRoomComplementaryMappings: selected.map(s => ({
                                    id: 0,
                                    roomId: prev.id || 0,
                                    rmcClientMappingId: rmcClientMappingId,
                                    complementaryId: s.value
                                }))
                            }))
                        }
                        isDisabled={readOnlyMode}
                    />
                </Col>
            </Row>
            */}
        </Form>
    );

    if (asDrawer) {
        return (
            <RightDrawer
                open={show}
                title={readOnlyMode && roomId ? `View Room – ${propertyName}` : roomId ? `Edit Room – ${propertyName}` : 'Create Room'}
                width={640}
                onClose={onClose}
                footer={<>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    {!readOnlyMode && (
                        <Button variant="primary" onClick={handleSave}>{roomId ? 'Update Room' : 'Create Room'}</Button>
                    )}
                </>}
            >
                {formContent}
            </RightDrawer>
        );
    }

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{readOnlyMode && roomId ? `View Room – ${propertyName}` : roomId ? `Edit Room – ${propertyName}` : 'Create Room'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{formContent}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                {!readOnlyMode && (
                    <Button variant="primary" onClick={handleSave}>{roomId ? 'Update Room' : 'Create Room'}</Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default RoomDetails;
